import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  LayoutDashboard, Users, Plus, Settings, LogOut, Menu, X,
  ChevronRight, Building2, Package, ShoppingCart, Bell, Tag, Truck, Check, FileText, Shield, Mail
} from 'lucide-react';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Tableau de bord', exact: true },
  { to: '/clients', icon: Users, label: 'Clients' },
  { to: '/commandes', icon: ShoppingCart, label: 'Commandes' },
  { to: '/suivi-carsat', icon: FileText, label: 'Suivi CARSAT' },
  { to: '/monitoring-email', icon: Shield, label: 'Monitoring Email' },
  { to: '/messagerie', icon: Mail, label: 'Messagerie' },
  { to: '/livraisons', icon: Truck, label: 'Planning' },
  { to: '/produits', icon: Tag, label: 'Produits' },
  { to: '/clients/nouveau', icon: Plus, label: 'Nouveau client' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);
  const [emailNotifs, setEmailNotifs] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [emailBannerDismissed, setEmailBannerDismissed] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const [emailRes, notifRes, emailNotifsRes] = await Promise.all([
        api.get('/email-notifs/unread-count').catch(() => ({ data: { count: 0 } })),
        api.get('/notifications/unread-count').catch(() => ({ data: { count: 0 } })),
        api.get('/email-notifs').catch(() => ({ data: [] })),
      ]);
      const newCount = emailRes.data?.count || 0;
      // If new emails arrived, re-show the banner
      if (newCount > unreadEmailCount && newCount > 0) {
        setEmailBannerDismissed(false);
      }
      setUnreadEmailCount(newCount);
      setNotifCount(notifRes.data?.count || 0);
      // Only keep unread email notifs for the banner
      const unread = (emailNotifsRes.data || []).filter(n => !n.read);
      setEmailNotifs(unread.slice(0, 5));
    } catch {}
  }, [unreadEmailCount]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data || []);
    } catch {}
  }, []);

  const handleMarkNotifRead = async (notifId) => {
    try {
      await api.put(`/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setNotifCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllNotifsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setNotifCount(0);
    } catch {}
  };

  const handleMarkEmailNotifRead = async (notifId) => {
    try {
      await api.put(`/email-notifs/${notifId}/read`);
      setEmailNotifs(prev => prev.filter(n => n.id !== notifId));
      setUnreadEmailCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarkAllEmailNotifsRead = async () => {
    try {
      await api.put('/email-notifs/read-all/all');
      setEmailNotifs([]);
      setUnreadEmailCount(0);
      setEmailBannerDismissed(true);
    } catch {}
  };

  useEffect(() => {
    fetchUnreadCount();
    fetchNotifications();
    const interval = setInterval(fetchUnreadCount, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, fetchNotifications]);

  const handleLogout = () => { logout(); navigate('/login'); };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-[hsl(214,32%,91%)]">
        <div className="w-9 h-9 bg-[hsl(222,47%,11%)] rounded-lg flex items-center justify-center flex-shrink-0">
          <Building2 size={18} className="text-white" />
        </div>
        {sidebarOpen && (
          <div>
            <p className="font-bold text-[hsl(222,47%,11%)] text-sm leading-tight">IBN CRM</p>
            <p className="text-xs text-gray-500">SAS IBN</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label, exact }) => (
          <NavLink
            key={to}
            to={to}
            end={exact}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group ${
                isActive
                  ? 'bg-[hsl(222,47%,11%)] text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`
            }
            data-testid={`nav-${label.toLowerCase().replace(/ /g,'-')}`}
          >
            <Icon size={18} className="flex-shrink-0" />
            {sidebarOpen && <span>{label}</span>}
          </NavLink>
        ))}

        {user?.role === 'admin' && (
          <NavLink
            to="/parametres"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive ? 'bg-[hsl(222,47%,11%)] text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              }`
            }
            data-testid="nav-parametres"
          >
            <Settings size={18} className="flex-shrink-0" />
            {sidebarOpen && <span>Paramètres</span>}
          </NavLink>
        )}
      </nav>

      {/* User footer */}
      <div className="p-3 border-t border-[hsl(214,32%,91%)]">
        <div className={`flex items-center gap-3 px-2 py-2 rounded-lg ${sidebarOpen ? '' : 'justify-center'}`}>
          <div className="w-8 h-8 bg-[hsl(222,47%,11%)] rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-semibold">{user?.prenom?.[0]}{user?.nom?.[0]}</span>
          </div>
          {sidebarOpen && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.prenom} {user?.nom}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
          )}
        </div>
        <button
          onClick={handleLogout}
          className={`mt-1 flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all ${!sidebarOpen ? 'justify-center' : ''}`}
          data-testid="logout-btn"
        >
          <LogOut size={16} />
          {sidebarOpen && <span>Déconnexion</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[hsl(210,20%,98%)]">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex flex-col bg-white border-r border-[hsl(214,32%,91%)] transition-all duration-200 ${sidebarOpen ? 'w-56' : 'w-16'}`}>
        <SidebarContent />
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)}>
          <aside className="w-56 h-full bg-white" onClick={e => e.stopPropagation()}>
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-[hsl(214,32%,91%)] px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => { setSidebarOpen(!sidebarOpen); setMobileOpen(!mobileOpen); }}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X size={20} className="hidden lg:block" /> : <Menu size={20} className="hidden lg:block" />}
            <Menu size={20} className="lg:hidden" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            {/* Bell notification */}
            <div className="relative">
              <button
                onClick={() => { setShowNotifPanel(!showNotifPanel); if (!showNotifPanel) fetchNotifications(); }}
                className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Notifications"
                data-testid="notif-bell-btn"
              >
                <Bell size={18} className={(notifCount + unreadEmailCount) > 0 ? 'text-blue-600' : 'text-gray-400'} />
                {(notifCount + unreadEmailCount) > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notif-badge">
                    {(notifCount + unreadEmailCount) > 9 ? '9+' : (notifCount + unreadEmailCount)}
                  </span>
                )}
              </button>

              {/* Notification panel */}
              {showNotifPanel && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowNotifPanel(false)} />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden" data-testid="notif-panel">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                      <p className="font-semibold text-sm text-gray-800">Notifications</p>
                      <button onClick={handleMarkAllNotifsRead} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="mark-all-notifs-read">
                        Tout marquer lu
                      </button>
                    </div>
                    <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                      {notifications.length === 0 && (
                        <div className="px-4 py-8 text-center text-gray-400 text-sm">Aucune notification</div>
                      )}
                      {notifications.slice(0, 15).map(n => (
                        <div key={n.id}
                          className={`px-4 py-3 hover:bg-gray-50 transition-colors ${!n.read ? 'bg-blue-50/40' : ''}`}
                          data-testid={`notif-item-${n.id}`}
                        >
                          <div className="flex items-start gap-2">
                            {!n.read && <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />}
                            {n.read && <span className="w-2 h-2 bg-transparent rounded-full mt-1.5 flex-shrink-0" />}
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => {
                              setShowNotifPanel(false);
                              if (n.type_tache === 'intervention') navigate(`/interventions/${n.tache_id}`);
                              else if (n.tache_id) navigate(`/commandes/${n.tache_id}`);
                            }}>
                              <p className={`text-sm ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{n.message}</p>
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                {n.created_at ? new Date(n.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                              </p>
                            </div>
                            {!n.read && (
                              <button onClick={(e) => { e.stopPropagation(); handleMarkNotifRead(n.id); }}
                                className="flex-shrink-0 p-1 rounded hover:bg-blue-100 text-blue-500 transition-colors"
                                title="Marquer lu"
                                data-testid={`mark-read-${n.id}`}>
                                <Check size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {unreadEmailCount > 0 && (
                      <div className="border-t border-gray-100 px-4 py-2 bg-gray-50">
                        <p className="text-xs text-gray-500">{unreadEmailCount} email{unreadEmailCount > 1 ? 's' : ''} non lu{unreadEmailCount > 1 ? 's' : ''}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Package size={14} />
              <span>IBN CRM v1.0</span>
            </div>
          </div>
        </header>

        {/* Email alert banner */}
        {unreadEmailCount > 0 && !emailBannerDismissed && (
          <div className="bg-blue-600 text-white px-4 py-2.5 flex items-center gap-3 shadow-lg animate-in slide-in-from-top" data-testid="email-banner">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Mail size={16} />
              </div>
              <span className="font-bold text-sm">{unreadEmailCount} nouv{unreadEmailCount > 1 ? 'eaux' : 'el'} email{unreadEmailCount > 1 ? 's' : ''}</span>
            </div>
            <div className="flex-1 overflow-x-auto flex items-center gap-2 min-w-0">
              {emailNotifs.map(n => (
                <button
                  key={n.id}
                  onClick={() => { handleMarkEmailNotifRead(n.id); navigate(`/clients/${n.client_id}`); }}
                  className="flex-shrink-0 flex items-center gap-2 px-3 py-1 bg-white/15 hover:bg-white/25 rounded-lg text-xs transition-colors max-w-[280px]"
                  data-testid={`email-banner-item-${n.id}`}
                >
                  <span className="font-medium truncate">{n.client_name || n.societe || '—'}</span>
                  <ChevronRight size={12} className="flex-shrink-0 opacity-60" />
                  <span className="truncate opacity-80">{n.subject || 'Email reçu'}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={handleMarkAllEmailNotifsRead}
                className="px-3 py-1 bg-white/15 hover:bg-white/25 rounded-lg text-xs font-medium transition-colors"
                data-testid="email-banner-read-all"
              >
                Tout marquer lu
              </button>
              <button
                onClick={() => setEmailBannerDismissed(true)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                data-testid="email-banner-dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
